/**
 * IntegrationGuide component
 * Provides tailored step-by-step instructions for each integration platform
 */

import React, { useRef, useState } from "react";
import {
  Button,
  Divider,
  Group,
  Image,
  Modal,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowRight,
  IconCheck,
  IconPlugConnected,
} from "@tabler/icons-react";

import { IntegrationLogos } from "./constants";
import type { IntegrationType } from "./types";
import {
  reloadExtensionOnboardingSteps,
  openExtensionToClientCreation,
  startExtensionIntegration,
} from "@utils/extensionClient";
import { emitDebugEvent } from "@utils/debug/eventIntegration";
import { openNewTab } from "@/utils/browserUtils";

interface IntegrationGuideProps {
  integrationType: IntegrationType;
  onComplete?: () => void;
}

interface IntegrationConfig {
  logo: string;
  name: string;
  canConnectFromFrontend: boolean;
  connectAction?: () => void;
  steps: {
    title: string;
    description: string;
    icon?: React.ReactNode;
    callout?: {
      type: "info" | "warning" | "success";
      title: string;
      message: string;
    };
  }[];
}

const GHLMarketplaceUrl =
  "https://marketplace.gohighlevel.com/oauth/chooselocation?response_type=code" +
  "&redirect_uri=https%3A%2F%2Fapp.smartwiz.io%2Fhome%2Flite%2Fsettings%3Ftab%3DIntegrations" +
  "&client_id=6904e6cdb48d9c96ef874f44-mhv6y3w3&scope=forms.readonly+oauth.readonly+medias.readonly" +
  "&version_id=6904e6cdb48d9c0102874f45";

// Jotform SDK reference (currently unused but may be needed for future features)
// const _Jotform = (window as any).JF;

/**
 * IntegrationGuide component
 * Displays step-by-step instructions for integrating with different platforms
 */
export default function IntegrationGuide({
  integrationType,
  onComplete: _onComplete,
}: IntegrationGuideProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [cognitoLoginModalOpen, setCognitoLoginModalOpen] = useState(false);
  const [cognitoIframeBlocked, setCognitoIframeBlocked] = useState(false);
  const [cognitoConnected, setCognitoConnected] = useState(false);
  const [jotformLoginModalOpen, setJotformLoginModalOpen] = useState(false);
  const [jotformIframeBlocked, setJotformIframeBlocked] = useState(false);
  const [jotformConnected, setJotformConnected] = useState(false);
  const [suitedashLoginModalOpen, setSuitedashLoginModalOpen] = useState(false);
  const [suitedashIframeBlocked, setSuitedashIframeBlocked] = useState(false);
  const [suitedashConnected, setSuitedashConnected] = useState(false);
  const [ghlLoginModalOpen, setGhlLoginModalOpen] = useState(false);
  const [ghlIframeBlocked, setGhlIframeBlocked] = useState(false);
  const [ghlConnected, setGhlConnected] = useState(false);
  const [extensionOpened, setExtensionOpened] = useState(false);
  const [autoConnectAttempted, setAutoConnectAttempted] = useState(false);
  const step2Ref = useRef<HTMLDivElement>(null);

  // Listen for integration connection messages from extension
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from the same origin
      if (event.origin !== window.location.origin) return;

      const message = event.data;

      if (message?.type === "INTEGRATION_CONNECTED") {
        console.log(
          "[IntegrationGuide] Integration connected:",
          message.integrationName
        );

        // Emit debug event for connection completion
        emitDebugEvent("action", "INTEGRATION_CONNECTION_COMPLETED", "web", {
          integrationName: message.integrationName,
        });

        // Update the appropriate connected state based on integration name
        switch (message.integrationName) {
          case "Jotform":
            setJotformConnected(true);
            break;
          case "Cognito Forms":
            setCognitoConnected(true);
            break;
          case "SuiteDash":
            setSuitedashConnected(true);
            break;
          case "Go High Level":
            setGhlConnected(true);
            break;
        }

        // Show success message
        console.log(
          `${message.integrationName} connected successfully! You can now go back to the home page.`
        );
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // Auto-trigger integration connection when extension opens
  React.useEffect(() => {
    if (extensionOpened && !autoConnectAttempted) {
      setAutoConnectAttempted(true);

      // Emit debug event for connection start
      emitDebugEvent("action", "INTEGRATION_CONNECTION_STARTED", "web", {
        integrationName: integrationType,
      });

      // Small delay to ensure extension is ready
      setTimeout(async () => {
        await startExtensionIntegration(integrationType);
      }, 500);
    }
  }, [extensionOpened, autoConnectAttempted, integrationType]);

  const handleJotformConnect = () => {
    if (jotformIframeBlocked) {
      // If iframe is blocked, open in popup window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      openNewTab(
        "https://www.jotform.com/login/",
        `width=${width},height=${height},left=${left},top=${top}`
      );
    } else {
      // Try iframe first
      setJotformLoginModalOpen(true);
    }
  };

  const handleJotformLoginComplete = async () => {
    setJotformLoginModalOpen(false);
    setJotformConnected(true);

    // Notify extension to reload onboarding steps
    try {
      await reloadExtensionOnboardingSteps();
    } catch (error) {
      console.error("Failed to reload extension onboarding steps:", error);
    }

    // Scroll to step 2 after a short delay
    setTimeout(() => {
      step2Ref.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 300);
  };

  const handleGHLConnect = () => {
    if (ghlIframeBlocked) {
      // If iframe is blocked, open in popup window
      const width = 800;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      openNewTab(
        GHLMarketplaceUrl,
        `width=${width},height=${height},left=${left},top=${top}`
      );
    } else {
      // Try iframe first
      setGhlLoginModalOpen(true);
    }
  };

  const handleGhlLoginComplete = async () => {
    setGhlLoginModalOpen(false);

    // Notify extension to reload onboarding steps
    try {
      await reloadExtensionOnboardingSteps();
    } catch (error) {
      console.error("Failed to reload extension onboarding steps:", error);
    }

    // Scroll to step 2 after a short delay
    setTimeout(() => {
      step2Ref.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 300);
  };

  const handleCognitoConnect = () => {
    if (cognitoIframeBlocked) {
      // If iframe is blocked, open in popup window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      openNewTab(
        "https://www.cognitoforms.com/login",
        `width=${width},height=${height},left=${left},top=${top}`
      );
    } else {
      // Try iframe first
      setCognitoLoginModalOpen(true);
    }
  };

  const handleCognitoLoginComplete = async () => {
    setCognitoLoginModalOpen(false);
    setCognitoConnected(true);

    // Notify extension to reload onboarding steps
    try {
      await reloadExtensionOnboardingSteps();
    } catch (error) {
      console.error("Failed to reload extension onboarding steps:", error);
    }

    // Scroll to step 2 after a short delay
    setTimeout(() => {
      step2Ref.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 300);
  };

  const handleSuitedashConnect = () => {
    if (suitedashIframeBlocked) {
      // If iframe is blocked, open in popup window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      openNewTab(
        "https://app.suitedash.com/",
        `width=${width},height=${height},left=${left},top=${top}`
      );
    } else {
      // Try iframe first
      setSuitedashLoginModalOpen(true);
    }
  };

  const handleSuitedashLoginComplete = async () => {
    setSuitedashLoginModalOpen(false);
    setSuitedashConnected(true);

    // Notify extension to reload onboarding steps
    try {
      await reloadExtensionOnboardingSteps();
    } catch (error) {
      console.error("Failed to reload extension onboarding steps:", error);
    }

    // Scroll to step 2 after a short delay
    setTimeout(() => {
      step2Ref.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 300);
  };

  const handleOpenExtension = async () => {
    setIsConnecting(true);

    try {
      // Notify extension to reload onboarding steps
      await reloadExtensionOnboardingSteps();

      // Open extension sidepanel to client creation page
      const success = await openExtensionToClientCreation();

      setIsConnecting(false);
      setExtensionOpened(success);

      // Scroll to step 2 after opening extension
      setTimeout(() => {
        step2Ref.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);

      // Don't call onComplete here - let user manually complete when done
    } catch (error) {
      console.error("Failed to open extension:", error);
      setIsConnecting(false);
    }
  };

  const integrationConfigs: Record<IntegrationType, IntegrationConfig> = {
    Jotform: {
      logo: IntegrationLogos.Jotform,
      name: "Jotform",
      canConnectFromFrontend: true,
      connectAction: handleJotformConnect,
      steps: [
        {
          title: "Log into Jotform",
          description:
            "Click the button below to log into your Jotform account. This will open a login window where you can securely sign in.",
        },
        {
          title: "Open the Client Creation Page",
          description:
            "Click the button below to open the SmartWiz extension sidepanel and navigate to the client creation page.",
        },
        {
          title: "Click the Jotform Icon",
          description:
            "In the extension, click the Jotform logo to connect and start importing clients from your Jotform submissions.",
        },
      ],
    },
    "Cognito Forms": {
      logo: IntegrationLogos["Cognito Forms"],
      name: "Cognito Forms",
      canConnectFromFrontend: true,
      connectAction: handleCognitoConnect,
      steps: [
        {
          title: "Log into Cognito Forms",
          description:
            "Click the button below to log into your Cognito Forms account. This will open a login window where you can securely sign in.",
        },
        {
          title: "Open the Client Creation Page",
          description:
            "Click the button below to open the SmartWiz extension sidepanel and navigate to the client creation page.",
        },
        {
          title: "Click the Cognito Forms Icon",
          description:
            "In the extension, click the Cognito Forms logo to connect and start importing clients from your forms.",
        },
      ],
    },
    "Go High Level": {
      logo: IntegrationLogos["Go High Level"],
      name: "Go High Level",
      canConnectFromFrontend: true,
      connectAction: handleGHLConnect,
      steps: [
        {
          title: "Connect to GHL Marketplace",
          description:
            "Click the button below to open the Go High Level marketplace, select your location, and authorize SmartWiz.",
        },
        {
          title: "Open the Client Creation Page",
          description:
            "Click the button below to open the SmartWiz extension sidepanel and navigate to the client creation page.",
        },
        {
          title: "Click the Go High Level Icon",
          description:
            "In the extension, click the Go High Level logo to connect and start importing clients from your contacts.",
        },
      ],
    },
    SuiteDash: {
      logo: IntegrationLogos.SuiteDash,
      name: "SuiteDash",
      canConnectFromFrontend: true,
      connectAction: handleSuitedashConnect,
      steps: [
        {
          title: "Log into SuiteDash",
          description:
            "Click the button below to log into your SuiteDash account. This will open a login window where you can securely sign in.",
        },
        {
          title: "Open the Client Creation Page",
          description:
            "Click the button below to open the SmartWiz extension sidepanel and navigate to the client creation page.",
        },
        {
          title: "Click the SuiteDash Icon",
          description:
            "In the extension, click the SuiteDash logo to connect and start importing clients from your portal.",
        },
      ],
    },
  };

  const config = integrationConfigs[integrationType];

  return (
    <Stack gap="xl" style={{ maxWidth: 800, margin: "0 auto" }}>
      {/* Header with logo */}
      <Stack gap="md">
        <Group align="center">
          <Image
            src={config.logo}
            alt={config.name}
            width={48}
            height={48}
            fit="contain"
          />
          <Title order={2}>Connect {config.name}</Title>
        </Group>
        <Text c="dimmed" size="sm">
          Follow these steps to integrate {config.name} with SmartWiz and start
          importing client data automatically.
        </Text>
      </Stack>

      <Divider />

      {/* All steps displayed continuously */}
      <Stack gap="xl">
        {config.steps.map((step, index) => (
          <Stack key={index} gap="lg" ref={index === 1 ? step2Ref : undefined}>
            <Group align="flex-start" wrap="nowrap">
              <ThemeIcon size={56} radius="md" variant="light" color="blue">
                <Text fw={700} size="xl">
                  {index + 1}
                </Text>
              </ThemeIcon>
              <Stack gap="xs" style={{ flex: 1 }}>
                <Title order={3}>{step.title}</Title>
                <Text size="md" c="dimmed">
                  {step.description}
                </Text>
              </Stack>
            </Group>

            {/* Action button for first step */}
            {index === 0 && (
              <Group justify="center">
                {config.canConnectFromFrontend ? (
                  (() => {
                    const isConnected =
                      (integrationType === "Jotform" && jotformConnected) ||
                      (integrationType === "Cognito Forms" &&
                        cognitoConnected) ||
                      (integrationType === "SuiteDash" && suitedashConnected) ||
                      (integrationType === "Go High Level" && ghlConnected);

                    return (
                      <Tooltip
                        label={`${config.name} integration is already connected`}
                        disabled={!isConnected}
                        position="bottom"
                      >
                        <Button
                          size="lg"
                          leftSection={
                            isConnected ? (
                              <IconCheck size={20} />
                            ) : (
                              <IconPlugConnected size={20} />
                            )
                          }
                          onClick={config.connectAction}
                          color={isConnected ? "green" : "blue"}
                          disabled={isConnected}
                        >
                          {isConnected
                            ? "Connected!"
                            : `Connect ${config.name}`}
                        </Button>
                      </Tooltip>
                    );
                  })()
                ) : (
                  <Button
                    size="lg"
                    leftSection={<IconArrowRight size={20} />}
                    onClick={handleOpenExtension}
                    loading={isConnecting}
                  >
                    Open Extension to Connect
                  </Button>
                )}
              </Group>
            )}

            {/* Action button for second step - Open Extension */}
            {index === 1 && (
              <Group justify="center">
                <Button
                  size="lg"
                  leftSection={
                    extensionOpened ? (
                      <IconCheck size={20} />
                    ) : (
                      <IconArrowRight size={20} />
                    )
                  }
                  onClick={handleOpenExtension}
                  loading={isConnecting}
                  color={extensionOpened ? "green" : "blue"}
                >
                  {extensionOpened ? "Extension Opened!" : "Open Extension"}
                </Button>
              </Group>
            )}

            {/* Show icon for third step - what to click in extension */}
            {index === 2 && (
              <Group justify="center">
                <Stack align="center" gap="md">
                  <Text size="sm" c="dimmed" fw={500}>
                    Look for this icon in the extension:
                  </Text>
                  <Image
                    src={config.logo}
                    alt={`${config.name} icon`}
                    width={96}
                    height={96}
                    fit="contain"
                  />
                  <Text size="xs" c="dimmed" ta="center" maw={300}>
                    Click this icon in the extension to start importing your{" "}
                    {config.name} data
                  </Text>
                </Stack>
              </Group>
            )}

            {/* Divider between steps (except last one) */}
            {index < config.steps.length - 1 && <Divider />}
          </Stack>
        ))}
      </Stack>

      {/* Cognito Forms Login Modal */}
      <Modal
        opened={cognitoLoginModalOpen}
        onClose={() => setCognitoLoginModalOpen(false)}
        title="Log into Cognito Forms"
        size="lg"
        centered
      >
        <Stack gap="md">
          {!cognitoIframeBlocked ? (
            <>
              <iframe
                src="https://www.cognitoforms.com/login"
                style={{
                  width: "100%",
                  height: "400px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  pointerEvents: "auto",
                }}
                title="Cognito Forms Login"
                onError={() => {
                  console.error("Cognito Forms iframe blocked");
                  setCognitoIframeBlocked(true);
                }}
              />
              <Stack
                gap="sm"
                style={{
                  position: "sticky",
                  bottom: 0,
                  backgroundColor: "white",
                  padding: "16px 0",
                  marginTop: "8px",
                }}
              >
                <Text size="sm" c="dimmed" ta="center">
                  After logging in successfully, click the button below.
                </Text>
                <Group justify="center">
                  <Button onClick={handleCognitoLoginComplete} size="md">
                    I've Logged In
                  </Button>
                </Group>
              </Stack>
            </>
          ) : (
            <Stack gap="md">
              <Text>
                Due to security restrictions, we cannot embed the Cognito Forms
                login page directly. Please log in using the popup window that
                opened, or click the button below to open it again.
              </Text>
              <Group justify="center">
                <Button
                  onClick={() => {
                    const width = 600;
                    const height = 700;
                    const left =
                      window.screenX + (window.outerWidth - width) / 2;
                    const top =
                      window.screenY + (window.outerHeight - height) / 2;
                    openNewTab(
                      "https://www.cognitoforms.com/login",
                      `width=${width},height=${height},left=${left},top=${top}`
                    );
                  }}
                >
                  Open Login Window
                </Button>
              </Group>
              <Text size="sm" c="dimmed" ta="center">
                After logging in, close the popup and click "I've Logged In"
                below.
              </Text>
              <Group justify="center">
                <Button onClick={handleCognitoLoginComplete}>
                  I've Logged In
                </Button>
              </Group>
            </Stack>
          )}
        </Stack>
      </Modal>

      {/* Jotform Login Modal */}
      <Modal
        opened={jotformLoginModalOpen}
        onClose={() => setJotformLoginModalOpen(false)}
        title="Log into Jotform"
        size="lg"
        centered
      >
        <Stack gap="md">
          {!jotformIframeBlocked ? (
            <>
              <iframe
                src="https://www.jotform.com/login/"
                style={{
                  width: "100%",
                  height: "400px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  pointerEvents: "auto",
                }}
                title="Jotform Login"
                onError={() => {
                  console.error("Jotform iframe blocked");
                  setJotformIframeBlocked(true);
                }}
              />
              <Stack
                gap="sm"
                style={{
                  position: "sticky",
                  bottom: 0,
                  backgroundColor: "white",
                  padding: "16px 0",
                  marginTop: "8px",
                }}
              >
                <Text size="sm" c="dimmed" ta="center">
                  After logging in successfully, click the button below.
                </Text>
                <Group justify="center">
                  <Button onClick={handleJotformLoginComplete} size="md">
                    I've Logged In
                  </Button>
                </Group>
              </Stack>
            </>
          ) : (
            <Stack gap="md">
              <Text>
                Due to security restrictions, we cannot embed the Jotform login
                page directly. Please log in using the popup window that opened,
                or click the button below to open it again.
              </Text>
              <Group justify="center">
                <Button
                  onClick={() => {
                    const width = 600;
                    const height = 700;
                    const left =
                      window.screenX + (window.outerWidth - width) / 2;
                    const top =
                      window.screenY + (window.outerHeight - height) / 2;
                    openNewTab(
                      "https://www.jotform.com/login/",
                      `width=${width},height=${height},left=${left},top=${top}`
                    );
                  }}
                >
                  Open Login Window
                </Button>
              </Group>
              <Text size="sm" c="dimmed" ta="center">
                After logging in, close the popup and click "I've Logged In"
                below.
              </Text>
              <Group justify="center">
                <Button onClick={handleJotformLoginComplete}>
                  I've Logged In
                </Button>
              </Group>
            </Stack>
          )}
        </Stack>
      </Modal>

      {/* SuiteDash Login Modal */}
      <Modal
        opened={suitedashLoginModalOpen}
        onClose={() => setSuitedashLoginModalOpen(false)}
        title="Log into SuiteDash"
        size="lg"
        centered
      >
        <Stack gap="md">
          {!suitedashIframeBlocked ? (
            <>
              <iframe
                src="https://app.suitedash.com/"
                style={{
                  width: "100%",
                  height: "400px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  pointerEvents: "auto",
                }}
                title="SuiteDash Login"
                onError={() => {
                  console.error("SuiteDash iframe blocked");
                  setSuitedashIframeBlocked(true);
                }}
              />
              <Stack
                gap="sm"
                style={{
                  position: "sticky",
                  bottom: 0,
                  backgroundColor: "white",
                  padding: "16px 0",
                  marginTop: "8px",
                }}
              >
                <Text size="sm" c="dimmed" ta="center">
                  After logging in successfully, click the button below.
                </Text>
                <Group justify="center">
                  <Button onClick={handleSuitedashLoginComplete} size="md">
                    I've Logged In
                  </Button>
                </Group>
              </Stack>
            </>
          ) : (
            <Stack gap="md">
              <Text>
                Due to security restrictions, we cannot embed the SuiteDash
                login page directly. Please log in using the popup window that
                opened, or click the button below to open it again.
              </Text>
              <Group justify="center">
                <Button
                  onClick={() => {
                    const width = 600;
                    const height = 700;
                    const left =
                      window.screenX + (window.outerWidth - width) / 2;
                    const top =
                      window.screenY + (window.outerHeight - height) / 2;
                    openNewTab(
                      "https://app.suitedash.com/",
                      `width=${width},height=${height},left=${left},top=${top}`
                    );
                  }}
                >
                  Open Login Window
                </Button>
              </Group>
              <Text size="sm" c="dimmed" ta="center">
                After logging in, close the popup and click "I've Logged In"
                below.
              </Text>
              <Group justify="center">
                <Button onClick={handleSuitedashLoginComplete}>
                  I've Logged In
                </Button>
              </Group>
            </Stack>
          )}
        </Stack>
      </Modal>

      {/* Go High Level Login Modal */}
      <Modal
        opened={ghlLoginModalOpen}
        onClose={() => setGhlLoginModalOpen(false)}
        title="Connect to Go High Level"
        size="xl"
        centered
      >
        <Stack gap="md">
          {!ghlIframeBlocked ? (
            <>
              <iframe
                src={GHLMarketplaceUrl}
                style={{
                  width: "100%",
                  height: "500px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  pointerEvents: "auto",
                }}
                title="Go High Level Marketplace"
                onError={() => {
                  console.error("Go High Level iframe blocked");
                  setGhlIframeBlocked(true);
                }}
              />
              <Stack
                gap="sm"
                style={{
                  position: "sticky",
                  bottom: 0,
                  backgroundColor: "white",
                  padding: "16px 0",
                  marginTop: "8px",
                }}
              >
                <Text size="sm" c="dimmed" ta="center">
                  After authorizing SmartWiz, click the button below.
                </Text>
                <Group justify="center">
                  <Button onClick={handleGhlLoginComplete} size="md">
                    I've Authorized
                  </Button>
                </Group>
              </Stack>
            </>
          ) : (
            <Stack gap="md">
              <Text>
                Due to security restrictions, we cannot embed the Go High Level
                marketplace directly. Please authorize using the popup window
                that opened, or click the button below to open it again.
              </Text>
              <Group justify="center">
                <Button
                  onClick={() => {
                    const width = 800;
                    const height = 700;
                    const left =
                      window.screenX + (window.outerWidth - width) / 2;
                    const top =
                      window.screenY + (window.outerHeight - height) / 2;
                    openNewTab(
                      GHLMarketplaceUrl,
                      `width=${width},height=${height},left=${left},top=${top}`
                    );
                  }}
                >
                  Open Marketplace
                </Button>
              </Group>
              <Text size="sm" c="dimmed" ta="center">
                After authorizing, close the popup and click "I've Authorized"
                below.
              </Text>
              <Group justify="center">
                <Button onClick={handleGhlLoginComplete}>
                  I've Authorized
                </Button>
              </Group>
            </Stack>
          )}
        </Stack>
      </Modal>
    </Stack>
  );
}
